<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { t } from 'i18next';
import Dialog from '@/components/Dialog/Dialog.vue';
import {
  TaskTypeDescription,
  QaRtSystemTemplateContainerDto,
  QaRtSystemTemplateContainerEntryDto,
} from '@/api/generator';
import { RTSystemTileSelectionType } from '@/components/RTSystemTile/RTSystemTileSelectionType';
import { I18nKey } from '@/utils/typings';
import RTSystemTile from '@/components/RTSystemTile/RTSystemTile.vue';
import TextField from '@/components/forms/TextField/TextField.vue';
import SearchField from '@/components/filter/SearchField.vue';
import Autocomplete from '@/components/forms/Autocomplete/Autocomplete.vue';
import { NotificationMessage } from '@/components/notifications/types';
import Notification from '@/components/notifications/Notification.vue';

interface QaSectionSystemSelectionProps {
  name: string;
  modelValue: boolean;
  taskType: TaskTypeDescription;
  qaRtSystemTemplateContainerDtos: QaRtSystemTemplateContainerDto[];
  groupName?: string;
  supportSystemEntityIds?: Array<string> | null | undefined;
  notification?: NotificationMessage;
  alreadyUsedIDs?: string[];
  alreadyUsedGroupNames?: string[];
}

const props = defineProps<QaSectionSystemSelectionProps>();

const filteredContainers = ref(props.qaRtSystemTemplateContainerDtos);

const groupName = ref<string>(props.groupName || '');

const emits = defineEmits<{
  submit: [string[], string];
  'update:modelValue': [boolean];
}>();

const selectedIds = ref<string[]>(
  props.supportSystemEntityIds ? [...props.supportSystemEntityIds] : [],
);

const handleDialogClose = () => {
  emits('update:modelValue', false);
};

const submitHandler = () => {
  emits('submit', [...selectedIds.value], groupName.value);
};

const handleItemClick = (
  itemId: string,
  multiSelect: boolean,
  containerEntries: Array<QaRtSystemTemplateContainerEntryDto>,
) => {
  if (!itemId) return;

  const index = selectedIds.value.indexOf(itemId);
  const idHasNotBeenAddedYet = index === -1;

  if (multiSelect) {
    // Toggle the selection status of the item
    if (idHasNotBeenAddedYet) {
      selectedIds.value.push(itemId);
    } else {
      selectedIds.value.splice(index, 1);
    }
  } else if (idHasNotBeenAddedYet) {
    const containerEntryIds = new Set(containerEntries.map((entry) => entry.system?.id || ''));

    // Remove any selected item that is part of the current container
    selectedIds.value = selectedIds.value.filter((id) => !containerEntryIds.has(id));

    // Add the new itemId
    selectedIds.value.push(itemId);
  } else {
    // Remove the itemId if it is already selected
    selectedIds.value.splice(index, 1);
  }
};

const checkForValidGroupName = () => {
  const hasLength = groupName.value?.length > 0;
  return hasLength && !nameIsAlreadyUsed.value;
};

const applyButtonEnabled = computed<boolean>(
  () =>
    selectedIds.value.length > 0 &&
    (props.taskType === 'BeamConstancyCheck' || checkForValidGroupName()),
);

const nameIsAlreadyUsed = computed(
  () => props.alreadyUsedGroupNames && props.alreadyUsedGroupNames.includes(groupName.value),
);

const rules = {
  required: (value: string) => !!value || t('Validation.PropertyRequired'),
  groupNameUnique: () => !nameIsAlreadyUsed.value || t('GroupNameAlreadyExistsMsg'),
};

// filter

const keywordFilter = ref('');
const brandOneFilter = ref<Array<string>>();
const subcategoryFilter = ref<Array<string>>();

const selectableSystems = computed(() =>
  props.qaRtSystemTemplateContainerDtos
    .flatMap((container) => container.entries ?? [])
    .flatMap((entry) => (entry.system ? [entry.system] : []))
    .filter((system) => !props.alreadyUsedIDs?.includes(system.id)),
);

const manufacturers = computed(
  () => new Set(selectableSystems.value.map((system) => system.brand1).filter(Boolean)),
);
const subcategories = computed(
  () => new Set(selectableSystems.value.map((system) => system.subCategory).filter(Boolean)),
);

const afterEnterHandler = () => {
  brandOneFilter.value = [];
  subcategoryFilter.value = [];

  groupName.value = props.groupName || '';
  selectedIds.value = props.supportSystemEntityIds ? [...props.supportSystemEntityIds] : [];

  filteredContainers.value = props.qaRtSystemTemplateContainerDtos;
};

const afterLeaveHandler = () => {
  groupName.value = '';
  selectedIds.value = [];
  keywordFilter.value = '';
};

watch([keywordFilter, brandOneFilter, subcategoryFilter], () => {
  const keyword = keywordFilter.value?.trim().toLowerCase();

  filteredContainers.value = props.qaRtSystemTemplateContainerDtos.map((container) => {
    const filteredEntries = container.entries?.filter((entry) => {
      const matchesKeyword = entry.system?.name.toLowerCase().includes(keyword) || !keyword;

      const matchesBrandOne =
        brandOneFilter.value && brandOneFilter.value.length > 0
          ? brandOneFilter.value.some((brandOne) => entry.system?.brand1?.includes(brandOne))
          : true;

      const matchesSubcategory =
        subcategoryFilter.value && subcategoryFilter.value.length > 0
          ? subcategoryFilter.value.some((type) => entry.system?.subCategory.includes(type))
          : true;

      return matchesKeyword && matchesBrandOne && matchesSubcategory;
    });

    return { ...container, entries: filteredEntries };
  });
});
function toggleSystemTileSelection(container: QaRtSystemTemplateContainerDto, systemId?: string) {
  if (!systemId) {
    throw new Error('systemId is required');
  }
  handleItemClick(systemId, container.isMultiSelect || false, container.entries || []);
}
</script>

<template>
  <Dialog
    :modelValue="modelValue"
    size="large"
    @update:modelValue="handleDialogClose"
    @afterEnter="afterEnterHandler"
    @afterLeave="afterLeaveHandler"
  >
    <template #title>
      <div style="flex: 1">
        <div class="flex">
          <h3>{{ $t('SelectDosimetryAndQaEquipment') }}</h3>
          <p class="text-body-1 text-primary-darken-2">
            {{ $t('YouCanOnlySelectDosimetryAndQaEquipmentThatHasBeenAdded') }}
          </p>
        </div>
        <!-- filter and search  -->
        <div class="my-2">
          <!-- Notification -->
          <Notification v-if="notification" :value="notification" :closable="false" class="pa-4" />
          <!-- search  -->
          <div v-if="taskType === 'ManualTask'" class="d-flex w-100 ga-4">
            <SearchField
              v-model="keywordFilter"
              :placeholder="$t('SearchPlaceholder')"
              class="w-33"
            />

            <Autocomplete
              v-model="brandOneFilter"
              :label="$t('Brand')"
              :items="
                [...manufacturers].map((item) => ({
                  value: item,
                  title: t(`BrandEnum.${item}` as I18nKey),
                }))
              "
              :placeholder="$t('All')"
              :maxNumDisplayItems="2"
              multiple
              clearable
              chips
              class="w-33"
            />

            <Autocomplete
              v-model="subcategoryFilter"
              :label="$t('Type_one')"
              :items="
                [...subcategories].map((item) => ({
                  value: item,
                  title: t(`QaEquipmentSubCategoryEnum.${item}` as I18nKey),
                }))
              "
              :placeholder="$t('All')"
              :maxNumDisplayItems="2"
              multiple
              clearable
              chips
              class="w-33"
            />
          </div>
        </div>
      </div>
    </template>

    <template #text>
      <div
        v-for="(container, index) in filteredContainers"
        :key="container.name || index"
        class="section-container"
      >
        <h4 v-if="taskType === 'BeamConstancyCheck'" class="mb-6 mt-12">
          {{ $t(container.name as I18nKey) }}
        </h4>

        <div v-if="container.entries && container.entries.length > 0" class="tile-container mt-6">
          <RTSystemTile
            v-for="item in container.entries"
            v-show="!alreadyUsedIDs?.includes(item.system?.id || '')"
            :key="item.system?.id"
            :dontShowBadges="true"
            :dontShowDeviceState="true"
            :item="item.system"
            :selected="selectedIds.some((id) => id === item.system?.id)"
            :selectionType="
              container.isMultiSelect || container.entries?.length === 1
                ? RTSystemTileSelectionType.multiSelect
                : RTSystemTileSelectionType.singleSelect
            "
            @click="toggleSystemTileSelection(container, item.system?.id)"
          />
        </div>
        <template v-if="!container.entries || container.entries.length === 0">
          <p class="text-primary-darken-2 mb-6 mt-0">
            {{ $t('NoItemsHaveBeenAddedToDosimetryAndQa') }}
          </p>
        </template>
      </div>
    </template>

    <template #actions>
      <div class="actions-container">
        <TextField
          v-if="taskType === 'ManualTask'"
          :label="$t('AssignGroupName')"
          class="w-50"
          :modelValue="groupName"
          :placeholder="$t('Required')"
          :rules="[rules.required, rules.groupNameUnique]"
          @update:modelValue="(mv: string) => (groupName = mv)"
        />

        <VSpacer />

        <TextVBtn :text="$t('Cancel')" class="mr-4" @click="handleDialogClose" />
        <SecondaryVBtn :disabled="!applyButtonEnabled" :text="$t('Apply')" @click="submitHandler" />
      </div>
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
.tile-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
}
.actions-container {
  display: flex;
  align-items: end;

  width: 100%;
  height: 80px;
}

.custom-title-text {
  font-size: 16px;
  font-weight: 100;
  margin-bottom: 20px;
  color: rgb(87, 96, 101);
}
</style>
